import { useCallback, useContext, useEffect } from 'react';
import { UNSAFE_NavigationContext, useLocation, useNavigate } from 'react-router-dom';
import i18n from '../i18n';

let activeWarningsCount = 0;
let skipNextNavigationPrompt = false;

function isModifiedClick(event) {
  return event.metaKey || event.altKey || event.ctrlKey || event.shiftKey || event.button !== 0;
}

function getAnchor(event) {
  return event.target instanceof Element ? event.target.closest('a[href]') : null;
}

function getUrlLocation(url) {
  return `${url.pathname}${url.search}${url.hash}`;
}

function allowNextNavigation() {
  skipNextNavigationPrompt = true;
  setTimeout(() => {
    skipNextNavigationPrompt = false;
  }, 0);
}

export function hasUnsavedChangesWarning() {
  return activeWarningsCount > 0;
}

export function confirmUnsavedChangesNavigation() {
  if (!hasUnsavedChangesWarning()) {
    return true;
  }

  if (skipNextNavigationPrompt) {
    skipNextNavigationPrompt = false;
    return true;
  }

  const confirmed = window.confirm(i18n.t('message.unsavedChangesLeavePage'));
  if (confirmed) {
    allowNextNavigation();
  }

  return confirmed;
}

export function useGuardedNavigate() {
  const navigate = useNavigate();

  return useCallback((to, options) => {
    if (confirmUnsavedChangesNavigation()) {
      navigate(to, options);
    }
  }, [navigate]);
}

export function useUnsavedChangesWarning(isDirty) {
  const location = useLocation();
  const { navigator } = useContext(UNSAFE_NavigationContext);

  useEffect(() => {
    if (!isDirty) {
      return undefined;
    }

    activeWarningsCount += 1;

    return () => {
      activeWarningsCount = Math.max(0, activeWarningsCount - 1);
    };
  }, [isDirty]);

  useEffect(() => {
    if (!isDirty) return undefined;

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
      return '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  useEffect(() => {
    if (!isDirty || typeof navigator.block !== 'function') {
      return undefined;
    }

    const unblock = navigator.block((transition) => {
      if (confirmUnsavedChangesNavigation()) {
        unblock();
        transition.retry();
      }
    });

    return unblock;
  }, [isDirty, navigator]);

  useEffect(() => {
    if (!isDirty || typeof navigator.block === 'function') {
      return undefined;
    }

    const handleClick = (event) => {
      if (event.defaultPrevented || isModifiedClick(event)) {
        return;
      }

      const anchor = getAnchor(event);
      if (!anchor || anchor.target || anchor.hasAttribute('download')) {
        return;
      }

      const url = new URL(anchor.href, window.location.href);
      if (url.origin !== window.location.origin) {
        return;
      }

      const currentLocation = `${location.pathname}${location.search}${location.hash}`;
      if (getUrlLocation(url) === currentLocation) {
        return;
      }

      if (!confirmUnsavedChangesNavigation()) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
      }
    };

    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [isDirty, location, navigator]);
}
